import { withErrorBoundary } from 'app/components/error-boundary';
import { OnboardingLayout } from 'app/components/onboarding/layout';
import { Column } from 'app/design/primitives/box';
import { useScopedTranslation } from 'app/hooks/i18n';
import { PersonalInformationFormFields, PhoneFieldHelpModal } from 'app/screens/onboarding/personal-information.shared';
import { useOnboardingNavigation } from 'app/screens/onboarding/credentials';
import { usePersonalInformationForm } from 'app/screens/onboarding/hooks/use-personal-information-form';
import { usePersonalInformationSubmitFlow } from 'app/screens/onboarding/hooks/use-personal-information-submit-flow';
import type { NavigationScreen } from 'app/types/navigation';
import { usePageTitle } from 'app/utils/web-helpers';
import { useEffect, useRef, useState } from 'react';
import type { TextInput } from 'react-native';
import { RemoteError } from 'app/design/primitives/remote-error';

const PersonalInformationScreenComponent: NavigationScreen<'personal-information'> = () => {
  const { t } = useScopedTranslation();
  const { control, handleSubmit, userInfo } = usePersonalInformationForm();
  const { isSubmitLoading, onSubmit, error } = usePersonalInformationSubmitFlow({ handleSubmit });

  const setPageTitle = usePageTitle('personal-information');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const { progressActiveColor } = useOnboardingNavigation(userInfo?.type, { headerPresentation: 'solid' });

  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const tSuffix =
    userInfo?.type === 'public' && t('firstNameFieldLabel-public') !== 'firstNameFieldLabel-public' ? '-public' : '';

  const refAdditionalName = useRef<TextInput>(null);
  const refLastName = useRef<TextInput>(null);
  const refPhoneNumber = useRef<TextInput>(null);

  const formFieldsProps = {
    control,
    onOpenPhoneInfo: () => setIsInfoOpen(true),
    refAdditionalName,
    refLastName,
    refPhoneNumber,
    t,
    tSuffix,
    userInfo,
  };

  const primaryProps = {
    loading: isSubmitLoading,
    onPress: () => void onSubmit(),
  };

  const phoneHelpModal = <PhoneFieldHelpModal isOpen={isInfoOpen} t={t} onClose={() => setIsInfoOpen(false)} />;

  return (
    <OnboardingLayout activeColor={progressActiveColor} primary={primaryProps}>
      <Column gap="spacing.general.x4">
        <PersonalInformationFormFields {...formFieldsProps} />
        {error ? <RemoteError {...{ error, message: error.message }} /> : null}
      </Column>
      {phoneHelpModal}
    </OnboardingLayout>
  );
};

export const PersonalInformationScreen: NavigationScreen<'personal-information'> = withErrorBoundary(
  PersonalInformationScreenComponent,
);
